/**
 * Branding API Error Message Parsing
 *
 * Every non-2xx response on the branding endpoints carries the same body: a
 * single `message` string. There is no error code, no field list and no
 * discriminator.
 *
 * Colour validation reports every offending slot, but joined into that one
 * string, each part prefixed `colors.<slot>: ` and separated by `; `. Splitting
 * it is therefore a best-effort read of an unstructured string rather than
 * parsing a documented format — so the full message is always retained, and
 * anything unattributable is shown whole. This is a limitation of the API, not a
 * hint that a richer shape exists.
 *
 * Requirements: 11.5, 11.6, 11.7
 */

import {
  BRAND_COLOR_SLOTS,
  type BrandColorSlot,
} from '../models/domainModels';

/** Separator the backend joins colour failures with. */
const SEGMENT_SEPARATOR = '; ';

/** Prefix identifying a colour slot failure. */
const COLOR_PREFIX = 'colors.';

export interface ParsedBrandingError {
  /** The complete message as the backend sent it. Always populated. */
  readonly message: string;
  /**
   * Per-slot detail, where it could be attributed.
   *
   * Empty for most errors, which are not per-slot at all. Callers must fall back
   * to displaying `message` when this is empty.
   */
  readonly slotMessages: ReadonlyMap<BrandColorSlot, string>;
}

const isKnownSlot = (value: string): value is BrandColorSlot =>
  (BRAND_COLOR_SLOTS as readonly string[]).includes(value);

/**
 * Attribute one `colors.<slot>: detail` segment, if that is what it is.
 *
 * Only the first colon separates the slot from the detail, because the detail
 * itself contains colons — `must match #RRGGBB, got: 'red'`.
 */
function parseSegment(
  segment: string
): readonly [BrandColorSlot, string] | null {
  if (!segment.startsWith(COLOR_PREFIX)) return null;

  const separatorIndex = segment.indexOf(':', COLOR_PREFIX.length);
  if (separatorIndex === -1) return null;

  const slot = segment.slice(COLOR_PREFIX.length, separatorIndex);
  if (!isKnownSlot(slot)) return null;

  return [slot, segment.slice(separatorIndex + 1).trim()];
}

/**
 * Parse a branding error message into the whole text plus any per-slot detail.
 *
 * A slot name outside this app's vocabulary is not attributed — there is no input
 * to attach it to — but it stays in `message`, so a backend that added a ninth
 * colour cannot produce an error with no visible cause.
 */
export function parseBrandingError(message: string): ParsedBrandingError {
  const slotMessages = new Map<BrandColorSlot, string>();

  message.split(SEGMENT_SEPARATOR).forEach((segment) => {
    const parsed = parseSegment(segment.trim());
    if (parsed !== null) {
      slotMessages.set(parsed[0], parsed[1]);
    }
  });

  return { message, slotMessages };
}
